import {
  ActiveFilters,
  FilterType,
  Table as WWTable,
  useMultiselection,
} from "@120wateraudit/waterworks";
import React, { useCallback, useMemo, useContext } from "react";
import { intersection } from "lodash";
import { useTableState } from "src/hooks/useTableState";
import Sort from "src/types/Sort";
import { CNCSubmission, SubmissionStatus } from "src/types/Submission";
import { gotoSystem } from "src/utils/system";
import { ModalContext } from "src/modules/Modal";
import ActionMenu from "src/components/ActionMenu";
import { trueFalseListOptions } from "src/utils/selectList";
import { formatDate } from "src/utils/format";
import { useGetAllCNCSubmissionsQuery } from "src/services/state-submission";
import SubmissionStatusDropdown from "../Submissions/SubmissionStatusDropdown";
import { useOpenStatusChangeModal } from "../Submissions/useOpenStatusChangeModal";

interface TableProps {
  defaultFilters?: ActiveFilters;
  systemId?: string;
  submissionPeriodId?: number;
  isOnUtilityDetailsPage?: boolean;
}

export const Table: React.FC<TableProps> = ({
  defaultFilters,
  systemId,
  submissionPeriodId,
  isOnUtilityDetailsPage,
}) => {
  const { params, setFilters, setPage, setPageSize, setSearchTerm, setSort } =
    useTableState(defaultFilters);

  const { onClearSelection, onSelect, onSelectAll, selectedIds } =
    useMultiselection<CNCSubmission>();

  const { closeModal, openModal } = useContext(ModalContext);
  const { data: submissionQueryData, refetch } = useGetAllCNCSubmissionsQuery({
    submissionPeriodId,
    searchParams: {
      ...params,
      filters: { ...params.filters, pwsId: systemId },
    },
  });

  const submissions = submissionQueryData?.data ?? [];

  const openChangeSubmissionCommunicationStatus = useOpenStatusChangeModal();
  const areAllSelected = useMemo(() => {
    const idsOnPage = submissions.map((s) => s.id);
    return intersection(idsOnPage, selectedIds).length === idsOnPage.length;
  }, [submissions, selectedIds]);

  const selectAllData = useCallback(() => {
    onSelectAll(submissions, areAllSelected);
  }, [onSelectAll, submissions, areAllSelected]);

  const COLUMNS = [
    {
      Header: "Submission Period Name",
      name: "SubmissionPeriod",
      key: "submissionPeriod",
      accessor: (s: CNCSubmission) => s.submissionPeriod?.name,
    },
    {
      Header: "Due Date",
      name: "DueDate",
      key: "submissionPeriod.dueDate",
      accessor: (s: CNCSubmission) => formatDate(s.submissionPeriod?.dueDate),
    },
    {
      Header: "Submitted Date",
      name: "Submitted Date",
      key: "submittedOn",
      sortable: true,
      accessor: (s: CNCSubmission) => formatDate(s.submittedOn),
    },
    {
      Header: "Status",
      name: "Status",
      key: "status",
      sortable: true,
      accessor: (s: CNCSubmission) => (
        <SubmissionStatusDropdown
          openChangeSubmissionStatus={openChangeSubmissionCommunicationStatus}
          status={s?.status ?? "notSubmitted"}
          submissionId={s?.id ?? 0}
          pwsName={s.system.name ?? ""}
          pwsId={s.pwsId ?? 0}
          submissionPeriodId={s.submissionPeriodId ?? 0}
          tooltipText="Change the Customer Notice Submission Status"
          refetch={refetch}
        />
      ),
    },
    {
      Header: "Overdue",
      name: "Overdue",
      key: "overdue",
      sortable: true,
      accessor: (s: CNCSubmission) => (s.overdue ? "Yes" : "No"),
    },
    {
      Header: "Lead Notice Quantity",
      name: "Lead Notice Quantity",
      key: "data.leadNoticeDistributions",
      sortable: true,
      accessor: (s: CNCSubmission) => s.data?.leadNoticeDistributions,
    },
    {
      Header: "Lead Notice Date",
      name: "Lead Notice Date",
      key: "data.leadNoticeDistributionsDate",
      sortable: true,
      accessor: (s: CNCSubmission) =>
        formatDate(s.data?.leadNoticeDistributionsDate),
    },
    {
      Header: "GRR Notice Quantity",
      name: "GRR Notice Quantity",
      key: "data.grrNoticeDistributions",
      sortable: true,
      accessor: (s: CNCSubmission) => s.data?.grrNoticeDistributions,
    },
    {
      Header: "GRR Notice Date",
      name: "GRR Notice Date",
      key: "data.grrNoticeDistributionsDate",
      sortable: true,
      accessor: (s: CNCSubmission) =>
        formatDate(s.data?.grrNoticeDistributionsDate),
    },
    {
      Header: "Unknown Notice Quantity",
      name: "Unknown Notice Quantity",
      key: "data.unknownNoticeDistributions",
      sortable: true,
      accessor: (s: CNCSubmission) => s.data?.unknownNoticeDistributions,
    },
    {
      Header: "Unknown Notice Date",
      name: "Unknown Notice Date",
      key: "data.unknownNoticeDistributionsDate",
      sortable: true,
      accessor: (s: CNCSubmission) =>
        formatDate(s.data?.unknownNoticeDistributionsDate),
    },
  ];

  const FILTERS = [
    {
      label: "Status",
      key: "status",
      type: FilterType.ListSelection,
      defaultValue: "All",
      multipleSelections: false,
      scrollingSelections: true,
      options: Object.entries(SubmissionStatus).map(([key, val]) => ({
        id: key,
        value: key,
        text: val,
      })),
    },
    {
      label: "Overdue",
      key: "overdue",
      type: FilterType.ListSelection,
      defaultValue: "All",
      multipleSelections: false,
      options: trueFalseListOptions,
    },
    {
      label: "Lead Notice Quantity",
      key: "leadNoticeDistributions",
      type: FilterType.Number,
    },
    {
      label: "Lead Notice Date",
      key: "leadNoticeDistributionsDate",
      type: FilterType.DatePicker,
    },
    {
      label: "GRR Notice Quantity",
      key: "grrNoticeDistributions",
      type: FilterType.Number,
    },
    {
      label: "GRR Notice Date",
      key: "grrNoticeDistributionsDate",
      type: FilterType.DatePicker,
    },
    {
      label: "Unknown Notice Quantity",
      key: "unknownNoticeDistributions",
      type: FilterType.Number,
    },
    {
      label: "Unknown Notice Date",
      key: "unknownNoticeDistributionsDate",
      type: FilterType.DatePicker,
    },
    {
      label: "Submitted By",
      key: "submittedBy",
      type: FilterType.TextMatch,
    },
    {
      label: "Submitted Date",
      key: "submittedOn",
      type: FilterType.DatePicker,
    },
    {
      label: "Submission Period Name",
      key: "submissionPeriodName",
      type: FilterType.TextMatch,
    },
    {
      label: "Due Date",
      key: "submissionPeriodDueDate",
      type: FilterType.DatePicker,
    },
  ].sort((a, b) => a.label.localeCompare(b.label));

  const ROW_ACTIONS_COLUMN = [
    {
      Header: "",
      hiddenOnColumnPicker: true,
      key: "system.id",
      sortable: false,
      accessor: (s: CNCSubmission) => (
        <ActionMenu
          identifiers={{
            accountId: s.pwsId.toString(),
            submissionId: s.id.toString(),
          }}
          actions={getRowActions(s)}
          ariaLabel="CNC submission actions"
        />
      ),
    },
  ];

  const getRowActions = (submission: CNCSubmission): any => {
    const actions = [];
    actions.push({
      text: "Login to PWS Portal",
      onClick: gotoSystem,
      identifier: "accountId",
    });
    actions.push({
      text: "Show Activity",
      onClick: () =>
        openModal(
          "activityModal",
          {
            submission,
            modalSize: "large",
            onClose: async () => {
              closeModal();
            },
          },
          "window"
        ),
      identifier: "submissionId",
    });
    return actions;
  };

  return (
    <WWTable
      activeFilters={params.filters}
      allowHiddenColumns={true}
      columns={[...COLUMNS]}
      rowActionColumn={ROW_ACTIONS_COLUMN}
      data={submissions}
      filterable
      filters={[...FILTERS]}
      itemName="Submissions"
      loading={false}
      onFilterChanged={setFilters}
      onPageChanged={setPage}
      onPageSizeChanged={setPageSize}
      orderable={true}
      onSearchChanged={setSearchTerm}
      onSortChanged={setSort}
      page={params.page}
      pageSize={params.pageSize}
      paginated
      searchTerm={params.searchTerm}
      searchable
      sort={params.sort as Sort}
      sortable
      totalRecords={submissions.length}
      rowsClickable={isOnUtilityDetailsPage}
      areAllSelected={areAllSelected}
      selectionMode="multi"
      onClearSelection={onClearSelection}
      onSelect={onSelect}
      onSelectAll={selectAllData}
      maxHeight={"57vh"}
      stickyFirstColumn
      stickyHeader
      showOverflow={false}
    />
  );
};

export default Table;
